import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Building2, Check, Info, Printer, ShieldCheck } from 'lucide-react';
import { ApiError } from '../utils/apiClient';
import reportVerificationService from '../services/reportVerificationService';
import type { ReportVerificationCertificate } from '../types/reportVerification.types';
import {
  buildReportedByList,
  formatPatientDisplay,
  formatStatusBadge,
  hasReportContent,
  isVerifiedStatus,
  normalizeFindings,
  normalizeImpression,
} from '../utils/reportVerificationUtils';
import '../theme/index.css';
import './ReportVerification.css';

const DEFAULT_VERIFY_MESSAGE =
  'This is an authentic report issued by the facility below and unaltered since it was finalized.';

const DEFAULT_SIDEBAR_NOTE =
  'Compare these details with your printed or shared copy. This page confirms the record’s authenticity, not the copy in your hand.';

const DEFAULT_REPORT_DISCLAIMER =
  "This is a fictional sample report rendered for demonstration. In the live system this page is reached by scanning the report's verification QR; compare the details with your printed copy to confirm authenticity.";

interface MetaRowProps {
  label: string;
  value?: React.ReactNode;
}

function MetaRow({ label, value }: Readonly<MetaRowProps>) {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  return (
    <div className="report-verification-page__meta-row">
      <span className="report-verification-page__meta-label">{label}</span>
      <div className="report-verification-page__meta-value">{value}</div>
    </div>
  );
}

function ReportContent({ certificate }: { certificate: ReportVerificationCertificate }) {
  const { report } = certificate;
  const findings = normalizeFindings(report.findings);
  const impression = normalizeImpression(report.impression);

  if (!hasReportContent(report)) {
    return (
      <div className="report-verification-page__empty-report">
        Report content will appear here once it is available from the server.
      </div>
    );
  }

  return (
    <>
      {report.clinical_history ? (
        <section className="report-verification-page__report-section">
          <h3 className="report-verification-page__report-heading">CLINICAL HISTORY</h3>
          <p className="report-verification-page__report-text">{report.clinical_history}</p>
        </section>
      ) : null}

      {report.technique ? (
        <section className="report-verification-page__report-section">
          <h3 className="report-verification-page__report-heading">TECHNIQUE</h3>
          <p className="report-verification-page__report-text">{report.technique}</p>
        </section>
      ) : null}

      {findings.length > 0 ? (
        <section className="report-verification-page__report-section">
          <h3 className="report-verification-page__report-heading">FINDINGS</h3>
          {findings.map((section, index) => (
            <div
              key={`${section.title}-${index}`}
              className="report-verification-page__finding-block"
            >
              {section.title ? (
                <h4 className="report-verification-page__report-subheading">{section.title}</h4>
              ) : null}
              <p className="report-verification-page__report-text">{section.content}</p>
            </div>
          ))}
        </section>
      ) : null}

      {impression.length > 0 ? (
        <section className="report-verification-page__report-section">
          <h3 className="report-verification-page__report-heading">IMPRESSION</h3>
          <ol className="report-verification-page__impression-list">
            {impression.map((item, index) => (
              <li key={`${item}-${index}`}>{item}</li>
            ))}
          </ol>
        </section>
      ) : null}
    </>
  );
}

const ReportVerification: React.FC = () => {
  const { studyInstanceUid } = useParams<{ studyInstanceUid: string }>();
  const [certificate, setCertificate] = useState<ReportVerificationCertificate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.body.classList.add('report-verification-page--active');

    return () => {
      document.body.classList.remove('report-verification-page--active');
    };
  }, []);

  useEffect(() => {
    if (!studyInstanceUid) {
      setError('Study instance UID is missing from the URL.');
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchCertificate = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await reportVerificationService.getCertificate(studyInstanceUid);

        if (cancelled) {
          return;
        }

        if (!response.success || !response.data) {
          setError(response.message || 'Unable to load report verification details.');
          setCertificate(null);
          return;
        }

        setCertificate(response.data);
      } catch (err) {
        if (cancelled) {
          return;
        }

        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Unable to load report verification details.';

        setError(message);
        setCertificate(null);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchCertificate();

    return () => {
      cancelled = true;
    };
  }, [studyInstanceUid]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="report-verification-page">
        <div className="report-verification-page__state">
          <h1 className="report-verification-page__state-title">Loading report</h1>
          <p className="report-verification-page__state-message">
            Please wait while we fetch the verification details.
          </p>
        </div>
      </div>
    );
  }

  if (error || !certificate) {
    return (
      <div className="report-verification-page">
        <div className="report-verification-page__state">
          <h1 className="report-verification-page__state-title">Verification unavailable</h1>
          <p className="report-verification-page__state-message">
            {error || 'The requested report could not be verified.'}
          </p>
        </div>
      </div>
    );
  }

  const { facility, report, verification } = certificate;
  const isVerified = isVerifiedStatus(verification.status);
  const reportedBy = buildReportedByList(report);
  const patientDisplay = formatPatientDisplay(report);
  const statusBadge = formatStatusBadge(report.report_status, report.report_version);
  const hasPatientDisplay = Boolean(patientDisplay?.trim());
  const sidebarNote = certificate.footer_note?.trim() || DEFAULT_SIDEBAR_NOTE;
  const reportDisclaimer = report.content_disclaimer?.trim() || DEFAULT_REPORT_DISCLAIMER;

  return (
    <div className="report-verification-page">
      <div className="report-verification-page__shell">
        <header className="report-verification-page__topbar">
          <div className="report-verification-page__topbar-left">
            <h1 className="report-verification-page__page-title">Radiology Report</h1>
            {isVerified ? (
              <span className="report-verification-page__verified-badge">
                <Check aria-hidden="true" />
                Verified
              </span>
            ) : null}
            {report.report_id ? (
              <span className="report-verification-page__report-id">{report.report_id}</span>
            ) : null}
          </div>

          <button
            type="button"
            className="report-verification-page__print-btn"
            onClick={handlePrint}
          >
            <Printer
              size={16}
              aria-hidden="true"
            />
            Print
          </button>
        </header>

        <div className="report-verification-page__layout">
          <aside className="report-verification-page__sidebar">
            <section className="report-verification-page__sidebar-panel">
              <div className="report-verification-page__verify-section">
                <div className="report-verification-page__verify-icon-wrap">
                  <ShieldCheck aria-hidden="true" />
                </div>
                <h2 className="report-verification-page__verify-title">
                  {isVerified ? 'Report verified' : verification.status}
                </h2>
                <p className="report-verification-page__verify-message">{DEFAULT_VERIFY_MESSAGE}</p>
              </div>
              <div className="report-verification-page__sidebar-section">
                <div className="report-verification-page__facility">
                  <Building2
                    className="report-verification-page__facility-icon"
                    aria-hidden="true"
                  />
                  <div>
                    <p className="report-verification-page__facility-name">{facility.name}</p>
                    {facility.address ? (
                      <p className="report-verification-page__facility-address">
                        {facility.address}
                      </p>
                    ) : facility.tagline ? (
                      <p className="report-verification-page__facility-address">
                        {facility.tagline}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="report-verification-page__sidebar-section">
                <h3 className="report-verification-page__section-title">REPORT DETAILS</h3>
                <div className="report-verification-page__meta-list">
                  <MetaRow
                    label="Report ID"
                    value={report.report_id}
                  />
                  <MetaRow
                    label="Document type"
                    value={report.document_type || 'Radiology Report'}
                  />
                  {hasPatientDisplay ? (
                    <MetaRow
                      label="Patient"
                      value={patientDisplay}
                    />
                  ) : null}
                  <MetaRow
                    label="Finalized"
                    value={report.report_finalized_on}
                  />
                  {statusBadge ? (
                    <MetaRow
                      label="Status"
                      value={
                        <span className="report-verification-page__status-pill">{statusBadge}</span>
                      }
                    />
                  ) : null}
                </div>
              </div>

              {reportedBy.length > 0 ? (
                <div className="report-verification-page__sidebar-section">
                  <h3 className="report-verification-page__section-title">REPORTED BY</h3>
                  <div className="report-verification-page__staff-list">
                    {reportedBy.map(member => (
                      <div
                        key={`${member.role}-${member.name}`}
                        className="report-verification-page__staff-item"
                      >
                        <div className="report-verification-page__staff-avatar">
                          {member.initials}
                        </div>
                        <div>
                          <p className="report-verification-page__staff-name">{member.name}</p>
                          <p className="report-verification-page__staff-role">
                            {member.role}
                            {member.registration_no ? ` · Reg. ${member.registration_no}` : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="report-verification-page__sidebar-section report-verification-page__sidebar-section--note">
                <p className="report-verification-page__sidebar-note">
                  <Info aria-hidden="true" />
                  <span>{sidebarNote}</span>
                </p>
              </div>
            </section>
          </aside>

          <main className="report-verification-page__report-panel">
            <ReportContent certificate={certificate} />
            <p className="report-verification-page__content-disclaimer">{reportDisclaimer}</p>
          </main>
        </div>
      </div>
    </div>
  );
};

export default ReportVerification;
